import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { BASE_URL } from "../../../config/config";
import { useSelector } from "react-redux";
import Wrapper from "../../../components/Wrapper";
import TenantInfoBanner from "../../../components/TenantInfoBannerOwner";
import {
  noticeDaysFor,
  earliestMoveOutDate,
  laterDate,
} from "../../../utils/noticePeriod";

const UnregisterTenantPage = () => {
  const open = useSelector((state) => state.sidebar.open);

  const getId = window.location.pathname.replace("/unregister-tenant/", "");
  const [urlTenantId, urlFlatId] = getId.split("/");

  // ── Tenant data from Redux ────────────────────────────────────────
  const flats = useSelector(
    (state) => state?.validateUser?.UserValidate?.data?.user?.flats
  );

  const validateUSER = useSelector(
    (state) => state?.validateUser?.UserValidate?.data
  );

  const reduxRequests = validateUSER?.user?.requests || [];

  const tenantFromRequests = reduxRequests.find(
    (req) =>
      String(req.tenantId) === String(urlTenantId) &&
      String(req.flatId) === String(urlFlatId)
  );

  const property = flats
    ?.flatMap((item) => item.propertyDetail || [])
    ?.find(
      (prop) =>
        String(prop._id) === String(urlFlatId) &&
        String(prop.tenantId) === String(urlTenantId)
    );

  // Merge request + property
  const displayTenant = tenantFromRequests
    ? {
        ...tenantFromRequests,
        leaseDuration: property?.leaseDuration,
        noticePeriod: property?.noticePeriod,
        rentIncrease: property?.rentIncrease,
        rentDueDate: property?.rentDueDate,
        rentRelaxationDays: property?.rentRelaxationDays,
        lateChargesPerDay: property?.lateChargesPerDay,
        latePaymentPolicy: property?.latePaymentPolicy,
      }
    : property;
  // ── Notice period ────────────────────────────────────────────────
  // The owner is removing the tenant, so the owner's notice period applies.
  // This starts from the flat's own agreement value and is corrected to the
  // property's actual configured PropertySetting.ownerNoticePeriod once
  // `fetchDues` below returns it (see `termination-preview`'s `notice.required`)
  // — otherwise this page could silently disagree with what the owner
  // configured, and with what the server itself enforces.
  const [noticeDays, setNoticeDays] = useState(() =>
    noticeDaysFor({ flat: displayTenant, initiatedBy: "LANDLORD" })
  );
  const earliestMoveOut = earliestMoveOutDate(noticeDays);

  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (displayTenant?.moveInDate && displayTenant?.leaseDuration) {
      const effectiveDate = calculateEffectiveDate(
        displayTenant.moveInDate,
        displayTenant.leaseDuration
      );

      // The lease-end date is the default, but if it falls before the notice
      // period (or has already passed), it's pushed forward to the notice
      // date — otherwise the form would default to an invalid date.
      setDueDate(laterDate(effectiveDate, earliestMoveOut));
    } else {
      setDueDate(earliestMoveOut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTenant]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [reason, setReason] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);

  const [cancelLoading, setCancelLoading] = useState(null); // request id
  const calculateEffectiveDate = (moveInDate, leaseDuration) => {
    if (!moveInDate || !leaseDuration) return "";

    const monthsMap = {
      "6_months": 5,
      "11_months": 10,
      "1_year": 11,
      "2_years": 23,
    };

    if (leaseDuration === "flexible") return "";

    // moveInDate format: DD-MM-YYYY
    const [day, month, year] = moveInDate.split("-").map(Number);

    const date = new Date(year, month - 1, day);

    date.setMonth(date.getMonth() + monthsMap[leaseDuration]);

    return date.toISOString().split("T")[0]; // YYYY-MM-DD
  };
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("ots_token");

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE_URL}/unregister-requests/${urlTenantId}/${urlFlatId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(data.requests);
    } catch (err) {
      console.error(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleUnregister = async () => {
    if (!dueDate || !reason) {
      toast.error("Both move-out date and reason are required");
      return;
    }

    if (dueDate < earliestMoveOut) {
      toast.error(
        `The tenant must be given ${noticeDays} days' notice — the earliest possible date is ${earliestMoveOut}.`
      );
      return;
    }

    setSubmitLoading(true);

    try {
      const { data } = await axios.post(
        `${BASE_URL}/unregister-tenant/${urlTenantId}/${urlFlatId}`,
        {
          dueDate,
          reason,
          action: "CREATE",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // The server's message states that the notice was sent and which day
      // the property will be vacated — previously the dialog just closed silently.
      toast.success(data?.message || "Notice sent to tenant");

      setOpenDialog(false);
      setReason("");

      fetchRequests();
    } catch (err) {
      // The notice-period restriction surfaces here; it must be shown to the
      // user, otherwise the button appears to do nothing when clicked.
      toast.error(
        err.response?.data?.message || "Failed to send request"
      );
      console.error(err.response?.data || err.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = async (requestId) => {
    setCancelLoading(requestId);

    try {
      await axios.post(
        `${BASE_URL}/unregister-tenant/${urlTenantId}/${urlFlatId}`,
        { action: "CANCEL" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchRequests();
    } catch (err) {
      console.error(err.response?.data || err.message);
    } finally {
      setCancelLoading(null);
    }
  };

  // ── What the tenant still owes (bills, rent, late fees) ───────────────────
  // A move-out cannot complete until this is zero, so the owner sees it before
  // accepting rather than finding out afterwards.
  const [dues, setDues] = useState(null);
  const [respondLoading, setRespondLoading] = useState(null); // `${id}:${action}`
  const [duesDialog, setDuesDialog] = useState(null); // server `dues` after an accept

  const fetchDues = async () => {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/termination-preview/${urlFlatId}?initiatedBy=LANDLORD`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDues({ total: data?.outstandingDues || 0, items: data?.dues || [] });

      // The property's actually-configured notice period, same source the
      // tenant-side move-out page trusts — overrides the flat-agreement
      // guess above once it's known.
      const fromRules = Number(data?.preview?.notice?.required) || 0;
      if (fromRules > 0) setNoticeDays(fromRules);
    } catch (err) {
      console.error(err.response?.data || err.message);
    }
  };

  useEffect(() => {
    fetchDues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the picked date valid if the real notice period arrives after the
  // dialog is already open, or after the lease-based default was set.
  useEffect(() => {
    if (openDialog && (!dueDate || dueDate < earliestMoveOut)) {
      setDueDate(earliestMoveOut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, earliestMoveOut]);

  // Accept or reject a move-out request the tenant sent.
  const handleRespond = async (requestId, action) => {
    setRespondLoading(`${requestId}:${action}`);
    try {
      const { data } = await axios.post(
        `${BASE_URL}/unregister-tenant/${urlTenantId}/${urlFlatId}`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.duesPending) {
        setDuesDialog(data.dues);
      } else {
        toast.success(data?.message || "Done");
      }
      fetchRequests();
      fetchDues();
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed");
    } finally {
      setRespondLoading(null);
    }
  };

  const getStatusColor = (status) => {
    const map = {
      PENDING: "warning",
      ACCEPTED: "success",
      REJECTED: "error",
      CANCELLED: "default",
    };
    return map[status?.toUpperCase()] || "default";
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <Wrapper open={open} />
      <Box
        className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box mb={3}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ color: "#0f172a" }}
            >
              Tenant Unregister Requests
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage unregister requests for this tenant
            </Typography>
          </Box>

          {/* ✅ Tenant Info Banner */}
          <TenantInfoBanner tenant={displayTenant} />

          {/* Action Button */}
          <Box mb={3}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenDialog(true)}
              sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
            >
              + Create Unregister Request
            </Button>
          </Box>

          {dues && dues.total > 0 && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              <AlertTitle sx={{ fontWeight: 800 }}>
                Tenant owes Rs {dues.total.toLocaleString("en-US")}
              </AlertTitle>
              The tenancy will not end and the property will not go live again
              until this is cleared. If you've already received this payment
              outside OTS, approve that bill from Pending Bills.
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {dues.items.map((d) => (
                  <li key={d.id}>
                    {d.label} {d.cycleKey ? `(${d.cycleKey})` : ""} — Rs{" "}
                    {Number(d.payable).toLocaleString("en-US")}
                    {d.penalty > 0
                      ? ` (late fee Rs ${Number(d.penalty).toLocaleString("en-US")})`
                      : ""}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {/* Requests Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : requests.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: "center",
                border: "1px solid #e2e8f0",
                borderRadius: 3,
                background: "#f8fafc",
              }}
            >
              <Typography variant="h6" color="text.secondary" mb={1}>
                No Requests Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No unregister requests have been created for this tenant yet.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    {[
                      "Status",
                      "Requested By",
                      "Effective Date",
                      "Reason",
                      "Requested At",
                      "Action",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          color: "#374151",
                          fontSize: "0.8rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow
                      key={req._id}
                      sx={{
                        "&:hover": { backgroundColor: "#f8fafc" },
                        transition: "background 0.15s",
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={req.status}
                          color={getStatusColor(req.status)}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        {req.requestedBy == "LANDLORD"
                          ? "Property Owner"
                          : req.requestedBy}
                      </TableCell>
                      <TableCell sx={{ color: "#374151" }}>
                        {new Date(req.effectiveDate).toLocaleDateString(
                          "en-GB",
                          { day: "2-digit", month: "short", year: "numeric" }
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#374151", maxWidth: 200 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {req.reason}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                        {new Date(req.requestedAt).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>
                        {req.status === "PENDING" &&
                        req.requestedBy?.toUpperCase() === "LANDLORD" ? (
                          <Button
                            color="warning"
                            variant="contained"
                            size="small"
                            onClick={() => handleCancel(req._id)}
                            disabled={cancelLoading === req._id}
                            sx={{
                              fontWeight: 700,
                              borderRadius: 2,
                              minWidth: 90,
                            }}
                          >
                            {cancelLoading === req._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              "Cancel"
                            )}
                          </Button>
                        ) : req.status === "PENDING" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              color="success"
                              variant="contained"
                              size="small"
                              onClick={() => handleRespond(req._id, "ACCEPT")}
                              disabled={Boolean(respondLoading)}
                              sx={{ fontWeight: 700, borderRadius: 2 }}
                            >
                              {respondLoading === `${req._id}:ACCEPT` ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                "Accept"
                              )}
                            </Button>
                            <Button
                              color="error"
                              variant="outlined"
                              size="small"
                              onClick={() => handleRespond(req._id, "REJECT")}
                              disabled={Boolean(respondLoading)}
                              sx={{ fontWeight: 700, borderRadius: 2 }}
                            >
                              {respondLoading === `${req._id}:REJECT` ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                "Reject"
                              )}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>
      </Box>

      {/* Create Request Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
          Create Unregister Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={3}>
            The tenant must be given at least{" "}
            <strong>{noticeDays} days'</strong> notice to vacate. The server
            will not accept any date earlier than this.
          </Typography>
          <TextField
            label="Move-out date"
            type="date"
            fullWidth
            required
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            // Prevent picking a date earlier than the notice period — otherwise
            // the form gets filled out and submitted only for the server to return a 400.
            inputProps={{ min: earliestMoveOut }}
            helperText={`Earliest possible date: ${earliestMoveOut}`}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Reason"
            multiline
            rows={4}
            fullWidth
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for unregister request..."
          />
          <Box
            mt={2.5}
            p={2}
            sx={{
              background: "#fff5f5",
              border: "1px solid #fecaca",
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              ⚠️ This sends a notice to the tenant. Even after the tenant
              accepts, the property will be vacated on the exact date you've
              given — rent and bills will continue as normal until then.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={{ minWidth: 90 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleUnregister}
            disabled={!dueDate || !reason || submitLoading}
            sx={{ minWidth: 150, fontWeight: 700 }}
          >
            {submitLoading ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              "Send Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shown after Accept when unpaid dues are holding the move-out back */}
      <Dialog open={Boolean(duesDialog)} onClose={() => setDuesDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Move-out accepted — dues outstanding</DialogTitle>
        <DialogContent>
          <Typography mb={1}>
            The request was accepted, but the tenant still owes{" "}
            <strong>Rs {Number(duesDialog?.total || 0).toLocaleString("en-US")}</strong>.
            The tenancy will end and the property will go live again as soon as
            this is cleared. The tenant has been sent an email and notification.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {(duesDialog?.items || []).map((d) => (
              <li key={d.id}>
                {d.label} {d.cycleKey ? `(${d.cycleKey})` : ""} — Rs{" "}
                {Number(d.payable).toLocaleString("en-US")}
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuesDialog(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnregisterTenantPage;
